import React, { useEffect, useRef, useState } from "react";
import CountUp from "react-countup";
import { useInView, motion } from "framer-motion";

/**
 * ProgressArcCounter — large stat number with an animated progress arc around it.
 * Counts up when in view; arc draws in sync.
 */
export const ProgressArcCounter = ({ end, suffix = "", prefix = "", duration = 2.2, decimals = 0, percent = 1, label, testId }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [start, setStart] = useState(false);

  useEffect(() => {
    if (inView) setStart(true);
  }, [inView]);

  const r = 64;
  const c = 2 * Math.PI * r;
  const drawTo = c * Math.max(0.05, Math.min(1, percent));

  return (
    <div ref={ref} data-testid={testId} className="flex flex-col items-start gap-3">
      <div className="relative w-[150px] h-[150px] flex items-center justify-center">
        <svg width="150" height="150" className="absolute inset-0 -rotate-90">
          {/* background ring */}
          <circle
            cx="75"
            cy="75"
            r={r}
            fill="none"
            stroke="currentColor"
            strokeOpacity="0.10"
            strokeWidth="2"
          />
          {/* progress arc */}
          <motion.circle
            cx="75"
            cy="75"
            r={r}
            fill="none"
            stroke="url(#arc-grad)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray={c}
            initial={{ strokeDashoffset: c }}
            animate={start ? { strokeDashoffset: c - drawTo } : {}}
            transition={{ duration: duration, ease: "easeOut" }}
          />
          <defs>
            <linearGradient id="arc-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#7B2FBE" />
              <stop offset="100%" stopColor="#2DD4BF" />
            </linearGradient>
          </defs>
        </svg>
        {/* small accent dot at the end of arc (decorative) */}
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={start ? { opacity: 1, scale: 1 } : {}}
          transition={{ delay: duration * 0.95, duration: 0.3 }}
          className="absolute top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#2DD4BF]"
          style={{ boxShadow: "0 0 12px rgba(45,212,191,0.85)" }}
        />
        <div className="font-heading font-semibold tracking-tighter text-3xl md:text-4xl" style={{ color: "var(--text)" }}>
          {start ? (
            <CountUp end={end} duration={duration} decimals={decimals} prefix={prefix} suffix={suffix} />
          ) : (
            <>{prefix}0{suffix}</>
          )}
        </div>
      </div>
      <div className="text-sm md:text-base max-w-[200px]" style={{ color: "var(--text-muted)" }}>{label}</div>
    </div>
  );
};

export default ProgressArcCounter;
